import { forwardRef, type TextareaHTMLAttributes } from 'react'
import { AlertCircle, CheckCircle2 } from 'lucide-react'
import { cn } from '@/shared/lib/utils'

export type TextareaState = 'default' | 'error' | 'success'

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /**
   * Состояние поля ввода
   */
  state?: TextareaState
  /**
   * Подпись поля
   */
  label?: string
  /**
   * Сообщение об ошибке
   */
  errorMessage?: string
  /**
   * Сообщение об успехе
   */
  successMessage?: string
  /**
   * Подсказка под полем
   */
  hint?: string
  /**
   * Показывать счетчик символов
   */
  showCounter?: boolean
  /**
   * Максимальное количество символов
   */
  maxLength?: number
}

/**
 * UI компонент: Многострочное поле ввода
 * 
 * Базовый компонент textarea с поддержкой состояний,
 * валидации и мобильной оптимизации.
 * 
 * @example
 * ```tsx
 * <Textarea
 *   label="Комментарий"
 *   placeholder="Введите комментарий"
 *   rows={4}
 *   state={errors.comment ? 'error' : 'default'}
 *   errorMessage={errors.comment}
 *   showCounter
 *   maxLength={500}
 * />
 * ```
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      state = 'default',
      label,
      errorMessage,
      successMessage,
      hint,
      showCounter = false,
      maxLength,
      className,
      value,
      onChange,
      ...props
    },
    ref
  ) => {
    const currentLength = typeof value === 'string' ? value.length : 0
    const showLengthCounter = showCounter && maxLength

    const textareaStyles = cn(
      // Базовые стили
      'w-full px-4 py-3 rounded-xl border-2 transition-colors duration-150',
      'bg-white dark:bg-dark-card text-ink dark:text-white',
      'placeholder:text-gray-400 dark:placeholder:text-gray-500',
      'focus:outline-none',
      'disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-dark-elevated',
      'read-only:bg-gray-50 dark:read-only:bg-dark-elevated read-only:cursor-default',
      'resize-y', // Разрешить изменение размера только по вертикали
      // Минимальная высота для touch target
      'min-h-[48px]',
      // Минимальный размер шрифта для предотвращения zoom на iOS
      'text-base',
      // Каретка (курсор)
      'caret-primary dark:caret-primary-light',
      // Состояния
      {
        'border-silk dark:border-dark-border focus:border-primary dark:focus:border-primary-light':
          state === 'default',
        'border-error focus:border-error':
          state === 'error',
        'border-success focus:border-success':
          state === 'success',
      },
      className
    )

    const getStateIcon = () => {
      if (state === 'error') {
        return <AlertCircle className="h-5 w-5 text-error" aria-hidden="true" />
      }
      if (state === 'success') {
        return <CheckCircle2 className="h-5 w-5 text-success" aria-hidden="true" />
      }
      return null
    }

    const stateIcon = getStateIcon()

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={props.id}
            className="block text-sm font-medium text-ink dark:text-white mb-2"
          >
            {label}
            {props.required && <span className="text-error ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          <textarea
            ref={ref}
            className={textareaStyles}
            value={value}
            onChange={onChange}
            maxLength={maxLength}
            aria-invalid={state === 'error'}
            aria-describedby={
              errorMessage || successMessage || hint || showLengthCounter
                ? `${props.id}-description`
                : undefined
            }
            {...props}
          />

          {stateIcon && (
            <div
              className="absolute right-3 top-3 pointer-events-none"
              aria-hidden="true"
            >
              {stateIcon}
            </div>
          )}
        </div>

        <div
          id={`${props.id}-description`}
          className="mt-2 flex items-start justify-between gap-2"
        >
          <div
            className={cn(
              'text-sm',
              {
                'text-error': errorMessage,
                'text-success': successMessage,
                'text-gray-500 dark:text-gray-400': hint && !errorMessage && !successMessage,
              }
            )}
            role={errorMessage ? 'alert' : undefined}
          >
            {errorMessage || successMessage || hint}
          </div>

          {showLengthCounter && (
            <div
              className={cn(
                'text-sm whitespace-nowrap',
                {
                  'text-gray-500 dark:text-gray-400': currentLength < maxLength * 0.9,
                  'text-warning': currentLength >= maxLength * 0.9 && currentLength < maxLength,
                  'text-error': currentLength >= maxLength,
                }
              )}
            >
              {currentLength} / {maxLength}
            </div>
          )}
        </div>
      </div>
    )
  }
)

Textarea.displayName = 'Textarea'


